import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { slugify } from "@/lib/slugify";

/**
 * Tilgangsgrupper (VIB-2721 / VIB-2726).
 *
 * En gruppe er en navngitt samling MENNESKER, ikke noe arkivspesifikt — derfor
 * `access_groups` og ikke `document_groups`, og derfor snakker denne hooken om
 * grupper og medlemmer uten å nevne mapper. Koblingen gruppe ↔ mappe bor i
 * `use-document-folder-grants.ts`, som er den arkivspesifikke delen.
 *
 * Autorisasjonen ligger i RLS (`access_groups_write` / `access_group_members_write`
 * = `is_admin()`). Hooken kan altså ikke gi bort noe basen ikke ville godtatt;
 * UI-et skjuler knappene for de andre, men det er ikke sikkerhetsmekanismen.
 *
 * Loggen skrives av DB-triggerne (VIB-2726), ikke herfra: en logg som bare
 * føres når endringen går gjennom appen mangler alt som skjer utenom den — og
 * gruppene ble til nå vedlikeholdt med SQL.
 */
export interface AccessGroup {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  member_ids: string[];
}

export function useAccessGroups() {
  return useQuery({
    queryKey: ["access-groups"],
    queryFn: async (): Promise<AccessGroup[]> => {
      const { data, error } = await supabase
        .from("access_groups")
        .select("id, name, slug, created_at, access_group_members(user_id)")
        .is("deleted_at", null)
        .order("name");
      if (error) throw error;

      type Row = Omit<AccessGroup, "member_ids"> & {
        access_group_members: { user_id: string }[] | null;
      };
      // MERK: `access_group_members_select` viser bare DITT eget medlemskap om
      // du ikke er admin. Medlemslistene her er altså komplette for admin, og
      // bevisst ufullstendige for alle andre — panelet er admin-only.
      return ((data ?? []) as unknown as Row[]).map((r) => ({
        id: r.id,
        name: r.name,
        slug: r.slug,
        created_at: r.created_at,
        member_ids: (r.access_group_members ?? []).map((m) => m.user_id),
      }));
    },
  });
}

/** Les-feil som sier noe til brukeren i stedet for å gjengi SQL. */
function forklar(e: Error): string {
  if (/duplicate key|23505/i.test(e.message)) {
    return "Det finnes allerede en gruppe med dette navnet. Er den slettet, må navnet endres litt — slugen er fortsatt i bruk.";
  }
  if (/row-level security/i.test(e.message)) {
    return "Bare admin kan endre grupper.";
  }
  return e.message;
}

export function useCreateAccessGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; createdById: string }) => {
      const slug = slugify(input.name);
      if (!slug) throw new Error("Gruppenavnet må inneholde minst én bokstav.");

      const { data, error } = await supabase
        .from("access_groups")
        .insert({ name: input.name.trim(), slug, created_by: input.createdById })
        .select("id")
        .single();
      if (error) throw error;
      return data as { id: string };
    },
    onSuccess: () => toast.success("Gruppe opprettet"),
    onError: (e: Error) => toast.error(forklar(e)),
    onSettled: async () => {
      await invalidateFor(qc, "access_groups");
    },
  });
}

export function useRenameAccessGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name: string }) => {
      const navn = input.name.trim();
      if (!navn) throw new Error("Gruppa må ha et navn.");
      // Slugen beholdes med vilje. Den er en stabil nøkkel, og å endre den
      // ville brutt enhver referanse som peker på gruppa ved slug.
      const { error } = await supabase
        .from("access_groups")
        .update({ name: navn, updated_at: new Date().toISOString() })
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: () => toast.success("Gruppa fikk nytt navn"),
    onError: (e: Error) => toast.error(forklar(e)),
    onSettled: async () => {
      await invalidateFor(qc, "access_groups");
    },
  });
}

/**
 * Soft-delete via RPC. En vanlig `UPDATE … SET deleted_at` blir avvist for
 * ALLE roller fordi `access_groups_select` skjuler slettede rader, og Postgres
 * krever at den nye raden består SELECT-policyen (samme felle som VIB-2703 —
 * se 20261001164858_documents_soft_delete_rpc.sql).
 *
 * Medlems- og tildelingsradene beholdes. Tilgangen stopper likevel med én
 * gang, fordi `can_read_document_folder` krever `deleted_at IS NULL` på gruppa,
 * og snapshotet i loggen peker på radene som ligger igjen.
 */
export function useDeleteAccessGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { error } = await supabase.rpc("soft_delete_access_group", {
        p_id: input.id,
      });
      if (error) throw error;
    },
    onSuccess: () => toast.success("Gruppe slettet"),
    onError: (e: Error) => toast.error(forklar(e)),
    onSettled: async () => {
      await invalidateFor(qc, "access_groups");
    },
  });
}

export function useAddAccessGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { groupId: string; userId: string; addedBy: string }) => {
      const { error } = await supabase.from("access_group_members").insert({
        group_id: input.groupId,
        user_id: input.userId,
        added_by: input.addedBy,
      });
      if (error) throw error;
    },
    onSuccess: () => toast.success("Lagt til i gruppa"),
    onError: (e: Error) => toast.error(forklar(e)),
    onSettled: async () => {
      await invalidateFor(qc, "access_group_members");
    },
  });
}

export function useRemoveAccessGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { groupId: string; userId: string }) => {
      const { error } = await supabase
        .from("access_group_members")
        .delete()
        .eq("group_id", input.groupId)
        .eq("user_id", input.userId);
      if (error) throw error;
    },
    onSuccess: () => toast.success("Fjernet fra gruppa"),
    onError: (e: Error) => toast.error(forklar(e)),
    onSettled: async () => {
      await invalidateFor(qc, "access_group_members");
    },
  });
}
